import { useMemo } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import useItemHistory from '@//data/item-history/useItemHistory';
import useVehiclesByIds from './useVehiclesByIds';

// Convert ElasticVehicle to ElasticListVehicle format for ProductCard compatibility
const convertToListVehicle = (vehicle: ElasticVehicle): ElasticListVehicle => ({
  _id: vehicle._id,
  branches: vehicle.branches.map((branch) => ({
    id: branch.id,
    name: branch.name,
  })),
  deductibleVat: vehicle.deductibleVat,
  engineBaseType: vehicle.engineBaseType,
  featuredImage: vehicle.featuredImage
    ? {
        id: vehicle.featuredImage.id,
        sortOrder: vehicle.featuredImage.sortOrder,
        type: vehicle.featuredImage.type,
        files: vehicle.featuredImage.files.map((file) => ({
          id: file.id,
          sortOrder: file.sortOrder,
          url: file.url,
          formats: file.formats.map((format) => ({
            format: format.format as any,
            id: format.id,
            jpeg: format.jpeg,
            url: format.url,
            webp: format.webp,
          })),
        })),
      }
    : undefined,
  fuelTypes: vehicle.fuelTypes,
  fuelType: vehicle.fuelType,
  gearboxType: vehicle.gearboxType,
  hasAutomotiveAssociationPackaging: vehicle.hasAutomotiveAssociationPackaging,
  hasManufacturerPackaging: vehicle.hasManufacturerPackaging,
  inventoryStatusString: vehicle.inventoryStatusString as any,
  hasMrfPackaging: vehicle.hasMrfPackaging,
  itemCreated: vehicle.itemCreated,
  itemPublished: vehicle.itemPublished,
  leasingPrice: vehicle.leasingPrice,
  businessLeasingPrice: vehicle.businessLeasingPrice,
  itemUpdated: vehicle.itemUpdated,
  leasing: {
    issuer: '',
    returnLocation: '',
  },
  manufacturer: vehicle.manufacturer,
  mileage: vehicle.mileage,
  modelSeries: vehicle.modelSeries,
  modelYear: vehicle.modelYear,
  odometerReading: {
    source: vehicle.odometerReading?.source || '',
    unit: (vehicle.odometerReading?.unit as any) || 'Kilometer',
    updatedAt: vehicle.odometerReading?.updatedAt || '',
    value: vehicle.odometerReading?.value || 0,
  },
  oldPrice: vehicle.oldPrice || 0,
  position: vehicle.position
    ? {
        city: vehicle.position.city,
        location: {
          lat: vehicle.position.location.lat,
          lon: vehicle.position.location.lon,
        },
        street: vehicle.position.street,
        zip: vehicle.position.zip,
      }
    : undefined,
  price: vehicle.price,
  salesName: vehicle.salesName,
  shortDescription: vehicle.shortDescription,
  title: vehicle.title,
  status: vehicle.status,
});

const useRecentVehicles = (maxVehicles = 3) => {
  const itemHistory = useItemHistory();

  // Get the latest maxVehicles item IDs (they're ordered from newest first)
  const recentItemIds = useMemo(
    () => itemHistory.slice(0, maxVehicles).map((item) => item.id),
    [itemHistory, maxVehicles],
  );

  // Fetch vehicle details for the recent item IDs
  const query = useVehiclesByIds({ ids: recentItemIds });
  const vehicleResults = query.data;

  // Filter out failed results, convert to list format, and maintain original order
  const recentVehicles = useMemo(() => {
    if (!vehicleResults) return [];

    // Create a map for quick lookup and convert to list format
    const vehicleMap = new Map(
      vehicleResults
        .filter((result) => result.vehicle)
        .map((result) => [result.id, convertToListVehicle(result.vehicle!)]),
    );

    // Return vehicles in the same order as recentItemIds
    return recentItemIds.map((id) => vehicleMap.get(id)).filter(Boolean) as ElasticListVehicle[];
  }, [vehicleResults, recentItemIds]);

  return {
    recentVehicles,
    isLoading: query.isLoading,
    hasRecentVehicles: recentVehicles.length > 0,
  };
};

export default useRecentVehicles;
